import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import styles from './SettingsPage.module.css';

type SettingsTab = 'profile' | 'security' | 'notifications' | 'preferences';

export function SettingsPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');

  // Profile form
  const [profileForm, setProfileForm] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: '',
    jobTitle: '',
  });

  // Password form
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Notification preferences
  const [notifications, setNotifications] = useState({
    emailTaskAssigned: true,
    emailTaskCompleted: true,
    emailDocumentShared: true,
    emailWeeklyDigest: false,
    browserNotifications: true,
  });

  // UI preferences
  const [preferences, setPreferences] = useState({
    theme: 'system',
    compactMode: false,
    showWelcome: true,
    defaultView: 'list',
  });

  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleSaveProfile(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // API call would go here
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSuccess('Profile updated successfully');
    } catch (err) {
      setError('Failed to update profile');
    } finally {
      setSaving(false);
    }
  }

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // API call would go here
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSuccess('Password changed successfully');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setError('Failed to change password');
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveNotifications(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // API call would go here
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSuccess('Notification preferences saved');
    } catch (err) {
      setError('Failed to save notification preferences');
    } finally {
      setSaving(false);
    }
  }

  async function handleSavePreferences(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // API call would go here
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSuccess('Preferences saved');
    } catch (err) {
      setError('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  }

  const tabs: { id: SettingsTab; label: string; icon: JSX.Element }[] = [
    {
      id: 'profile',
      label: 'Profile',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      ),
    },
    {
      id: 'security',
      label: 'Security',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      ),
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
      ),
    },
    {
      id: 'preferences',
      label: 'Preferences',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Settings</h1>
        <p className={styles.subtitle}>Manage your account settings and preferences</p>
      </div>

      <div className={styles.content}>
        <nav className={styles.sidebar}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`${styles.tabButton} ${activeTab === tab.id ? styles.active : ''}`}
              onClick={() => {
                setActiveTab(tab.id);
                setError(null);
                setSuccess(null);
              }}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>

        <div className={styles.main}>
          {success && <div className={styles.success}>{success}</div>}
          {error && <div className={styles.error}>{error}</div>}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Profile Information</h2>
              <p className={styles.sectionDescription}>
                Update your personal information and contact details.
              </p>
              <form onSubmit={handleSaveProfile} className={styles.form}>
                <div className={styles.formRow}>
                  <Input
                    label="First Name"
                    value={profileForm.firstName}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, firstName: e.target.value }))}
                    required
                  />
                  <Input
                    label="Last Name"
                    value={profileForm.lastName}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, lastName: e.target.value }))}
                    required
                  />
                </div>
                <Input
                  label="Email Address"
                  type="email"
                  value={profileForm.email}
                  onChange={(e) => setProfileForm(prev => ({ ...prev, email: e.target.value }))}
                  required
                  disabled
                  hint="Contact your administrator to change your email"
                />
                <Input
                  label="Phone Number"
                  type="tel"
                  value={profileForm.phone}
                  onChange={(e) => setProfileForm(prev => ({ ...prev, phone: e.target.value }))}
                />
                <Input
                  label="Job Title"
                  value={profileForm.jobTitle}
                  onChange={(e) => setProfileForm(prev => ({ ...prev, jobTitle: e.target.value }))}
                />
                <div className={styles.formActions}>
                  <Button type="submit" disabled={saving}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Change Password</h2>
              <p className={styles.sectionDescription}>
                Update your password to keep your account secure.
              </p>
              <form onSubmit={handleChangePassword} className={styles.form}>
                <Input
                  label="Current Password"
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                  required
                />
                <Input
                  label="New Password"
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                  required
                  hint="Minimum 8 characters"
                />
                <Input
                  label="Confirm New Password"
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  required
                />
                <div className={styles.formActions}>
                  <Button type="submit" disabled={saving}>
                    {saving ? 'Changing...' : 'Change Password'}
                  </Button>
                </div>
              </form>

              <div className={styles.securityInfo}>
                <h3>Security Information</h3>
                <div className={styles.infoGrid}>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Last Password Change</span>
                    <span className={styles.infoValue}>Never</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Security Clearance</span>
                    <span className={styles.infoValue}>Level {user?.securityClearance || 1}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Notification Preferences</h2>
              <p className={styles.sectionDescription}>
                Choose how you want to be notified about activity.
              </p>
              <form onSubmit={handleSaveNotifications} className={styles.form}>
                <div className={styles.checkboxGroup}>
                  <h4>Email Notifications</h4>
                  <label className={styles.checkbox}>
                    <input
                      type="checkbox"
                      checked={notifications.emailTaskAssigned}
                      onChange={(e) => setNotifications(prev => ({ ...prev, emailTaskAssigned: e.target.checked }))}
                    />
                    <span>Task assigned to me</span>
                  </label>
                  <label className={styles.checkbox}>
                    <input
                      type="checkbox"
                      checked={notifications.emailTaskCompleted}
                      onChange={(e) => setNotifications(prev => ({ ...prev, emailTaskCompleted: e.target.checked }))}
                    />
                    <span>Task I submitted is completed</span>
                  </label>
                  <label className={styles.checkbox}>
                    <input
                      type="checkbox"
                      checked={notifications.emailDocumentShared}
                      onChange={(e) => setNotifications(prev => ({ ...prev, emailDocumentShared: e.target.checked }))}
                    />
                    <span>Document shared with me</span>
                  </label>
                  <label className={styles.checkbox}>
                    <input
                      type="checkbox"
                      checked={notifications.emailWeeklyDigest}
                      onChange={(e) => setNotifications(prev => ({ ...prev, emailWeeklyDigest: e.target.checked }))}
                    />
                    <span>Weekly activity digest</span>
                  </label>
                </div>

                <div className={styles.checkboxGroup}>
                  <h4>Browser Notifications</h4>
                  <label className={styles.checkbox}>
                    <input
                      type="checkbox"
                      checked={notifications.browserNotifications}
                      onChange={(e) => setNotifications(prev => ({ ...prev, browserNotifications: e.target.checked }))}
                    />
                    <span>Enable browser notifications</span>
                  </label>
                </div>

                <div className={styles.formActions}>
                  <Button type="submit" disabled={saving}>
                    {saving ? 'Saving...' : 'Save Preferences'}
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Preferences Tab */}
          {activeTab === 'preferences' && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Display Preferences</h2>
              <p className={styles.sectionDescription}>
                Customize how the application looks and behaves.
              </p>
              <form onSubmit={handleSavePreferences} className={styles.form}>
                <div className={styles.selectGroup}>
                  <label className={styles.selectLabel}>Theme</label>
                  <select
                    className={styles.select}
                    value={preferences.theme}
                    onChange={(e) => setPreferences(prev => ({ ...prev, theme: e.target.value }))}
                  >
                    <option value="system">System Default</option>
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                  </select>
                </div>

                <div className={styles.selectGroup}>
                  <label className={styles.selectLabel}>Default View</label>
                  <select
                    className={styles.select}
                    value={preferences.defaultView}
                    onChange={(e) => setPreferences(prev => ({ ...prev, defaultView: e.target.value }))}
                  >
                    <option value="list">List View</option>
                    <option value="grid">Grid View</option>
                  </select>
                </div>

                <div className={styles.checkboxGroup}>
                  <h4>Other Settings</h4>
                  <label className={styles.checkbox}>
                    <input
                      type="checkbox"
                      checked={preferences.compactMode}
                      onChange={(e) => setPreferences(prev => ({ ...prev, compactMode: e.target.checked }))}
                    />
                    <span>Compact mode (smaller spacing)</span>
                  </label>
                  <label className={styles.checkbox}>
                    <input
                      type="checkbox"
                      checked={preferences.showWelcome}
                      onChange={(e) => setPreferences(prev => ({ ...prev, showWelcome: e.target.checked }))}
                    />
                    <span>Show welcome message on dashboard</span>
                  </label>
                </div>

                <div className={styles.formActions}>
                  <Button type="submit" disabled={saving}>
                    {saving ? 'Saving...' : 'Save Preferences'}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
